import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { ArrowLeft, Users, Eye, MousePointerClick, Sparkles, TrendingUp, ChevronRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, sub, color, testId }) => (
  <div className="rounded-2xl border border-gray-100 p-4" data-testid={testId}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${color}`}>
      <Icon className="w-4 h-4 text-white" />
    </div>
    <p className="text-xl font-bold text-gray-900">{value}</p>
    <p className="text-[11px] text-gray-500">{label}</p>
    {sub && <p className="text-[10px] text-emerald-600 font-medium mt-0.5">{sub}</p>}
  </div>
);

export default function ProfessionalAnalytics() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/professional/analytics?days=${days}`)
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || "Errore caricamento"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const chartData = (data?.series || []).map((s) => ({
    ...s,
    label: s.day.slice(5).replace("-", "/"),
  }));

  const funnel = data?.funnel;
  const funnelSteps = funnel ? [
    { label: "Impression", value: funnel.impressions, rate: null },
    { label: "Click", value: funnel.clicks, rate: funnel.click_rate },
    { label: "Visite profilo", value: funnel.profile_views, rate: funnel.view_rate },
    { label: "Nuovi follower", value: funnel.follows, rate: funnel.follow_rate },
  ] : [];
  const maxFunnel = Math.max(1, ...funnelSteps.map((f) => f.value));

  return (
    <div className="min-h-screen bg-white" data-testid="pro-analytics-page">
      <Seo title="Dashboard ADE Pro · ADE" description="Statistiche del tuo profilo ADE Pro" />
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b flex items-center gap-3 px-4 py-3 safe-top">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="proanalytics-back">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="text-sm font-semibold text-gray-800 flex-1">Dashboard ADE Pro</h1>
        <div className="flex gap-1 bg-gray-100 rounded-full p-0.5">
          {[7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                days === d ? "bg-blue-600 text-white" : "text-gray-500"
              }`}
              data-testid={`proanalytics-period-${d}`}
            >
              {d}g
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="px-4 py-4 space-y-3 animate-pulse">
          <div className="grid grid-cols-2 gap-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-2xl" />)}</div>
          <div className="h-48 bg-gray-100 rounded-2xl" />
        </div>
      )}

      {!loading && error && (
        <p className="text-center text-sm text-gray-400 py-16" data-testid="proanalytics-error">{error}</p>
      )}

      {!loading && data && (
        <div className="px-4 py-4 space-y-5 pb-10">
          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard icon={Users} label="Follower totali" value={data.followers_total}
              sub={data.followers_net_change !== 0 ? `${data.followers_net_change > 0 ? "+" : ""}${data.followers_net_change} in ${data.period_days}g` : null}
              color="bg-blue-600" testId="proanalytics-followers" />
            <StatCard icon={Eye} label="Visite al profilo" value={data.totals.profile_views}
              color="bg-emerald-600" testId="proanalytics-views" />
            <StatCard icon={Sparkles} label="Impression" value={data.totals.impressions}
              color="bg-purple-600" testId="proanalytics-impressions" />
            <StatCard icon={MousePointerClick} label="Click sulla scheda" value={data.totals.clicks}
              color="bg-pink-600" testId="proanalytics-clicks" />
          </div>

          {/* Today (realtime) */}
          <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4" data-testid="proanalytics-today">
            <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide mb-2">Oggi · in tempo reale</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[
                ["Impression", data.today.impressions],
                ["Click", data.today.clicks],
                ["Visite", data.today.profile_views],
                ["Follower", data.today.follows],
              ].map(([l, v]) => (
                <div key={l}>
                  <p className="text-lg font-bold text-gray-900">{v}</p>
                  <p className="text-[10px] text-gray-500">{l}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Trend chart */}
          <div className="rounded-2xl border border-gray-100 p-4" data-testid="proanalytics-chart">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-900">Andamento ultimi {data.period_days} giorni</h3>
            </div>
            <div className="h-48 -ml-4">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} interval="preserveStartEnd" tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 12 }}
                    formatter={(v, name) => [v, { profile_views: "Visite profilo", impressions: "Impression", follows: "Nuovi follower" }[name] || name]}
                    labelFormatter={(l) => `Giorno ${l}`}
                  />
                  <Bar dataKey="profile_views" fill="#2563eb" radius={[3, 3, 0, 0]} maxBarSize={14} />
                  <Line type="monotone" dataKey="impressions" stroke="#a855f7" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="follows" stroke="#10b981" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2">
              <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-sm bg-blue-600" /> Visite</span>
              <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-purple-500" /> Impression</span>
              <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Follower</span>
            </div>
          </div>

          {/* Funnel */}
          <div className="rounded-2xl border border-gray-100 p-4" data-testid="proanalytics-funnel">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Funnel di conversione</h3>
            <div className="space-y-2.5">
              {funnelSteps.map((f, i) => (
                <div key={f.label} data-testid={`funnel-step-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">{f.label}</span>
                    <span className="text-xs font-semibold text-gray-900">
                      {f.value}
                      {f.rate != null && <span className="text-[10px] text-blue-600 font-medium ml-1.5">{f.rate}%</span>}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all"
                      style={{ width: `${Math.max(2, (f.value / maxFunnel) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-3">Le percentuali indicano la conversione rispetto allo step precedente.</p>
          </div>

          {/* Audience link */}
          <button
            onClick={() => navigate("/app/professional/audience")}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100"
            data-testid="proanalytics-audience-link"
          >
            Gestisci Audience <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
